import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  HostBinding,
  ViewChild,
  ElementRef,
  AfterViewChecked,
  Renderer2
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { ChatApiService } from '../../shared-services/chat-api.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DynamicEditTableComponent } from '../../chart-components/dynamic-edit-table/dynamic-edit-table.component';
import { StreamingTextComponent } from '../streaming-text/streaming-text.component';
import { ActivatedRoute } from '@angular/router';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
interface Message {
  text: string;
  sender: 'user' | 'agent';
  files?: File[];
  type?: 'text' | 'table' | 'option' | 'json';
  data?: any;
  formattedText?: string;
  rawJson?: string;
  copied?: boolean;
  loading?: boolean;
  completed?: boolean;
}

@Component({
  selector: 'app-chat',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatTooltipModule,
    DynamicEditTableComponent,
    StreamingTextComponent,
  ],
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
})
export class ChatComponent implements OnInit, OnDestroy, AfterViewChecked {
  @HostBinding('class.chat-standalone') isStandaloneChat = false;
  @Input() data: any;
  @ViewChild(DynamicEditTableComponent) private dynamicEditTable?: DynamicEditTableComponent;
  @ViewChild('chatBody') private chatBody!: ElementRef;

  messages: Message[] = [];
  currentMessage = '';
  isLoading = false;
  selectedFiles: File[] = [];
  isDarkTheme = false;

  title = 'Chat'; // Default title
  welcomeMessage = 'Hello! How can I help you today?';

  private subscriptions = new Subscription();
  private previousMessageCount = 0;
  private parentCardBodyEl: HTMLElement | null = null;
  private parentHostContainerEl: HTMLElement | null = null;
  private parentCardBodyPaddingInline: string | null = null;
  private parentHostContainerDisplayInline: string | null = null;
  private parentHostContainerFlexDirectionInline: string | null = null;
  private parentHostContainerHeightInline: string | null = null;
  private parentHostContainerMinHeightInline: string | null = null;
  private objectUrlMap = new Map<File, string>();

  constructor(
    private chatApiService:ChatApiService,
    private route: ActivatedRoute,
    private configService: ConfigService,
    private responsiveLayoutService: ResponsiveLayoutService,
    private hostRef: ElementRef<HTMLElement>,
    private renderer: Renderer2,
  ) {}

  ngOnInit(): void {
    this.attachChatCardBodyClass();
    // Ensure DPR zoom compensation and CSS variables are set
    // (needed when chat is loaded directly via reload, bypassing DashboardComponent)
    this.responsiveLayoutService.init();

    const configPath = this.resolveConfigPath();
    if (configPath) {
      this.configService.getApi(configPath, undefined, undefined, 'get').subscribe({
        next: (config: any) => {
          if (config) {
            this.title = config.title || this.title;
            this.welcomeMessage = config.welcomeMessage || this.welcomeMessage;

            // Initialize messages AFTER config is loaded to show correct welcome message
            if (this.messages.length === 0) {
              this.messages.push({ text: this.welcomeMessage, sender: 'agent' });
            }
          }
          this.initSession();
        },
        error: (err) => {
          console.error('Failed to load chat config:', err);
          // Fallback initialization
          if (this.messages.length === 0) {
            this.messages.push({ text: this.welcomeMessage, sender: 'agent' });
          }
          this.initSession();
        },
      });
    } else {
      // Fallback if no config file specified
      if (this.messages.length === 0) {
        this.messages.push({ text: this.welcomeMessage, sender: 'agent' });
      }
      this.initSession();
    }
  }

  private resolveConfigPath(): string | null {
    const widgetConfigPath =
      this.data?.chatConfigPath ??
      this.data?.configPath ??
      this.data?.config_path ??
      this.data?.configFile;

    const routeConfigPath = this.route.snapshot.data['configFile'];
    const rawPath = widgetConfigPath || routeConfigPath;

    if (!rawPath || typeof rawPath !== 'string') {
      return null;
    }

    const trimmedPath = rawPath.trim();
    if (!trimmedPath) {
      return null;
    }

    if (trimmedPath.startsWith('assets/')) {
      return trimmedPath;
    }

    return `assets/${trimmedPath}`;
  }

  private initSession(): void {
    this.chatApiService.createSession().then((observable) => {
      observable.subscribe({
        next: (response) => console.log('Session created:', response),
        error: (error) => console.error('Error creating session:', error),
      });
    });
  }

  ngAfterViewChecked() {
    if (this.messages.length !== this.previousMessageCount) {
      // Do not auto-scroll on the very first render (initial/welcome state).
      if (this.previousMessageCount === 0) {
        this.previousMessageCount = this.messages.length;
        return;
      }
      this.scrollToBottom();
      this.previousMessageCount = this.messages.length;
    }
  }

  toggleTheme(): void {
    this.isDarkTheme = !this.isDarkTheme;

    if (this.isDarkTheme) {
      document.body.classList.add('dark-theme');
    } else {
      document.body.classList.remove('dark-theme');
    }
  }

  scrollToBottom(): void {
    try {
      setTimeout(() => {
        const chatBodyEl = this.chatBody?.nativeElement;
        if (!chatBodyEl) {
          return;
        }

        // Auto-scroll only when content actually overflows the viewport.
        const hasOverflow = chatBodyEl.scrollHeight > chatBodyEl.clientHeight;
        if (!hasOverflow) {
          return;
        }

        chatBodyEl.scrollTop = chatBodyEl.scrollHeight;
      }, 0);
    } catch (err) {}
  }

  ngOnDestroy(): void {
    this.detachChatCardBodyClass();
    this.revokeAllPreviewUrls();
    this.subscriptions.unsubscribe();
  }

  private attachChatCardBodyClass(): void {
    this.parentHostContainerEl = this.hostRef.nativeElement.parentElement;
    if (this.parentHostContainerEl) {
      this.parentHostContainerDisplayInline = this.parentHostContainerEl.style.display || null;
      this.parentHostContainerFlexDirectionInline = this.parentHostContainerEl.style.flexDirection || null;
      this.parentHostContainerHeightInline = this.parentHostContainerEl.style.height || null;
      this.parentHostContainerMinHeightInline = this.parentHostContainerEl.style.minHeight || null;

      this.renderer.setStyle(this.parentHostContainerEl, 'display', 'flex');
      this.renderer.setStyle(this.parentHostContainerEl, 'flex-direction', 'column');
      this.renderer.setStyle(this.parentHostContainerEl, 'height', '100%');
      this.renderer.setStyle(this.parentHostContainerEl, 'min-height', '0');
    }

    this.parentCardBodyEl = this.hostRef.nativeElement.closest('.card-body') as HTMLElement | null;
    this.isStandaloneChat = !this.parentCardBodyEl;
    if (this.parentCardBodyEl) {
      this.renderer.addClass(this.parentCardBodyEl, 'dvm-chat-card-body--flush');
      this.parentCardBodyPaddingInline = this.parentCardBodyEl.style.padding || null;
      this.renderer.setStyle(this.parentCardBodyEl, 'padding', '0');
    }
  }

  private detachChatCardBodyClass(): void {
    this.isStandaloneChat = false;
    if (this.parentHostContainerEl) {
      if (this.parentHostContainerDisplayInline !== null) {
        this.renderer.setStyle(this.parentHostContainerEl, 'display', this.parentHostContainerDisplayInline);
      } else {
        this.renderer.removeStyle(this.parentHostContainerEl, 'display');
      }
      if (this.parentHostContainerFlexDirectionInline !== null) {
        this.renderer.setStyle(this.parentHostContainerEl, 'flex-direction', this.parentHostContainerFlexDirectionInline);
      } else {
        this.renderer.removeStyle(this.parentHostContainerEl, 'flex-direction');
      }
      if (this.parentHostContainerHeightInline !== null) {
        this.renderer.setStyle(this.parentHostContainerEl, 'height', this.parentHostContainerHeightInline);
      } else {
        this.renderer.removeStyle(this.parentHostContainerEl, 'height');
      }
      if (this.parentHostContainerMinHeightInline !== null) {
        this.renderer.setStyle(this.parentHostContainerEl, 'min-height', this.parentHostContainerMinHeightInline);
      } else {
        this.renderer.removeStyle(this.parentHostContainerEl, 'min-height');
      }

      this.parentHostContainerEl = null;
      this.parentHostContainerDisplayInline = null;
      this.parentHostContainerFlexDirectionInline = null;
      this.parentHostContainerHeightInline = null;
      this.parentHostContainerMinHeightInline = null;
    }

    if (this.parentCardBodyEl) {
      if (this.parentCardBodyPaddingInline !== null) {
        this.renderer.setStyle(this.parentCardBodyEl, 'padding', this.parentCardBodyPaddingInline);
      } else {
        this.renderer.removeStyle(this.parentCardBodyEl, 'padding');
      }
      this.renderer.removeClass(this.parentCardBodyEl, 'dvm-chat-card-body--flush');
      this.parentCardBodyEl = null;
      this.parentCardBodyPaddingInline = null;
    }
  }

  startNewSession(): void {
    this.messages = [];
    // this.messages.push({ text: 'Hello! How can I help you today?', sender: 'agent' });
    this.chatApiService.createSession().then((observable) => {
      observable.subscribe({
        next: (response) => console.log('New session created:', response),
        error: (error) => console.error('Error creating new session:', error),
      });
    });
    this.scrollToBottom();
  }

  sendMessageOnEnter(event: Event | KeyboardEvent): void {
    const keyboardEvent = event as KeyboardEvent; // cast to KeyboardEvent
    if (keyboardEvent.key === 'Enter' && !keyboardEvent.shiftKey) {
      keyboardEvent.preventDefault();
      this.sendMessage();
    }
  }

  formatText(text: string): string {
    let formatted = text;
    // bold
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong></strong>');
    // italic
    formatted = formatted.replace(/\*(.*?)\*/g, '<em></em>');

    // Convert hyphenated lists to HTML unordered lists
    // First, convert each hyphenated line to an <li> tag
    // This handles cases where a list starts on a new line or at the beginning of the text
    formatted = formatted.replace(/^-\s(.+)$/gm, '<li></li>');

    // Then, wrap consecutive <li> tags in a <ul> tag
    // The regex ensures that <ul></ul> wraps only blocks of <li> elements
    formatted = formatted.replace(/((?:<li>.*?<\/li>\s*)+)/g, '<ul></ul>');

    return formatted;
  }

  async sendMessage(): Promise<void> {
    if ((!this.currentMessage.trim() && this.selectedFiles.length === 0) || this.isLoading) {
      return;
    }

    let tableData: any[] | undefined;
    if (this.dynamicEditTable && this.dynamicEditTable.isTableEditable()) {
      tableData = this.dynamicEditTable.getTableData();

      // Find and remove the last message containing the table
      const tableMessageIndex = this.messages.map((m) => m.type).lastIndexOf('table');
      if (tableMessageIndex > -1) {
        this.messages.splice(tableMessageIndex, 1);
      }
    }

    let jsonData: any | undefined;
    const jsonMessageIndex = this.messages.map((m) => m.type).lastIndexOf('json');
    if (jsonMessageIndex > -1) {
      const jsonMessage = this.messages[jsonMessageIndex];
      try {
        if (jsonMessage.rawJson) {
          jsonData = JSON.parse(jsonMessage.rawJson);
        }
        this.messages.splice(jsonMessageIndex, 1);
      } catch (e) {
        console.error('Invalid JSON format:', e);
        // Optionally, show an error message to the user in the chat
        this.messages.push({
          text: 'Error: Invalid JSON format. Please correct it before sending.',
          sender: 'agent',
        });
        return;
      }
    }

    const userMessage: Message = {
      text: this.currentMessage,
      sender: 'user',
      files: [...this.selectedFiles],
    };

    this.messages.push(userMessage);
    const messageToApi = this.currentMessage.trim();
    this.currentMessage = '';
    this.selectedFiles = [];
    this.isLoading = true;

    const turnStartIndex = this.messages.length;
    let accumulatedResponse = '';

    (await this.chatApiService.sendMessage(messageToApi, tableData, jsonData, this.data?.apiUrl)).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        // Expecting response format: { message: "..." }
        const agentText = response?.message || 'No response from agent.';

        const agentMessage: Message = {
          text: agentText,
          sender: 'agent',
          formattedText: this.formatText(agentText),
        };
        this.messages.push(agentMessage);
        this.scrollToBottom();
      },
      error: (error: any) => {
        this.isLoading = false;
        console.error('API Error:', error);
        const errorMessage = error.message || 'Sorry, something went wrong.';
        this.messages.push({
          text: errorMessage,
          sender: 'agent',
          formattedText: errorMessage,
        });
        this.scrollToBottom();
      },
    });
  }

  onFileSelected(event: Event): void {
    const element = event.currentTarget as HTMLInputElement;
    const fileList: FileList | null = element.files;
    if (fileList) {
      for (let i = 0; i < fileList.length; i++) {
        this.selectedFiles.push(fileList[i]);
      }
    }
  }

  removeFile(fileToRemove: File): void {
    this.selectedFiles = this.selectedFiles.filter((file) => file !== fileToRemove);
  }

  isImageFile(file: File): boolean {
    return typeof file?.type === 'string' && file.type.startsWith('image/');
  }

  getFilePreviewUrl(file: File): string | null {
    if (!this.isImageFile(file)) {
      return null;
    }

    const cached = this.objectUrlMap.get(file);
    if (cached) {
      return cached;
    }

    const url = URL.createObjectURL(file);
    this.objectUrlMap.set(file, url);
    return url;
  }

  private revokeAllPreviewUrls(): void {
    this.objectUrlMap.forEach((url) => URL.revokeObjectURL(url));
    this.objectUrlMap.clear();
  }

  onOptionSelected(option: string): void {
    this.currentMessage = option;
    this.sendMessage();

    // Find and remove the last message containing options
    const optionMessageIndex = this.messages.map((m) => m.type).lastIndexOf('option');
    if (optionMessageIndex > -1) {
      this.messages.splice(optionMessageIndex, 1);
    }
  }

  copyJson(msg: Message): void {
    if (msg.rawJson) {
      navigator.clipboard
        .writeText(msg.rawJson)
        .then(() => {
          msg.copied = true;
          setTimeout(() => {
            msg.copied = false;
          }, 2000); // Hide message after 2 seconds
        })
        .catch((err) => {
          console.error('Failed to copy JSON: ', err);
        });
    }
  }
}
